import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import Header from "@/components/Header";
import TierBadge from "@/components/TierBadge";
import PhotoLightbox from "@/components/PhotoLightbox";
import api, { API, getAuthToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/pages/ClientCategoriesPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  MapPin,
  CurrencyDollar,
  ChatCircleText,
  CheckCircle,
  Clock,
  Wallet,
  ShieldCheck,
} from "@phosphor-icons/react";

const STATUS_META = {
  open: { label: "Open — accepting quotes", color: "#0A0F0D", bg: "#E5E2DB" },
  awaiting_admin_approval: { label: "Awaiting admin approval", color: "#8B5E34", bg: "#FDECD0" },
  awaiting_payment: { label: "Awaiting client payment", color: "#8B5E34", bg: "#FDECD0" },
  in_progress: { label: "In progress", color: "#FFFFFF", bg: "#1D4ED8" },
  awaiting_confirmation: { label: "Awaiting client confirmation", color: "#8B5E34", bg: "#FDECD0" },
  awaiting_release: { label: "Awaiting admin to release escrow", color: "#8B5E34", bg: "#FDECD0" },
  completed: { label: "Completed", color: "#FFFFFF", bg: "#0A0F0D" },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.open;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest"
      style={{ color: meta.color, backgroundColor: meta.bg }}
      data-testid={`job-status-${status}`}
    >
      <Clock size={10} weight="bold" />
      {meta.label}
    </span>
  );
};

const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [job, setJob] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [quotePrice, setQuotePrice] = useState("");
  const [quoteMessage, setQuoteMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: j }, { data: q }] = await Promise.all([
        api.get(`/jobs/${jobId}`),
        api.get(`/jobs/${jobId}/quotes`).catch(() => ({ data: [] })),
      ]);
      setJob(j);
      setQuotes(q);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const token = getAuthToken();
  const photos = (job?.photo_ids || []).map((id) => ({
    src: `${API}/uploads/${id}?auth=${token}`,
    alt: "Job photo",
  }));

  const isClientOwner = job && user && job.client_id === user.id;
  const isAssignedContractor = job && user && job.accepted_contractor_id === user.id;
  const isContractor = user?.role === "contractor";
  const myQuote = quotes.find((q) => q.contractor_id === user?.id);

  const submitQuote = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const price = Number(quotePrice);
      if (!(price >= 0)) throw new Error("Price must be a positive number");
      await api.post(`/jobs/${jobId}/quotes`, { price, message: quoteMessage });
      toast.success("Quote sent!");
      setQuotePrice("");
      setQuoteMessage("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const acceptQuote = async (quoteId) => {
    try {
      await api.post(`/jobs/${jobId}/accept-quote`, { quote_id: quoteId });
      toast.success("Quote accepted — awaiting admin approval");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const payEscrow = async () => {
    try {
      const { data } = await api.post("/payments/create-session", {
        job_id: jobId,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe hosted checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const markComplete = async () => {
    try {
      await api.post(`/jobs/${jobId}/mark-complete`);
      toast.success("Marked complete — awaiting client confirmation");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const confirmComplete = async () => {
    try {
      await api.post(`/jobs/${jobId}/confirm-complete`);
      toast.success("Confirmed — admin will release funds shortly");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF]">
        <Header />
        <p className="mx-auto max-w-3xl p-10 text-[#5C605E]">Loading…</p>
      </div>
    );
  }
  if (!job) return null;

  const cat = CATEGORIES.find((c) => c.id === job.category);

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="job-detail-page">
      <Toaster position="top-right" richColors />
      <Header />
      <PhotoLightbox
        open={lightboxOpen}
        photos={photos}
        index={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onIndex={setLightboxIndex}
      />

      <main className="mx-auto max-w-4xl px-6 py-12 md:px-10 md:py-16">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-[#5C605E] hover:text-[#0A0F0D]"
          data-testid="job-detail-back"
        >
          ← Back
        </Button>

        <div className="rounded-md border border-[#E5E2DB] bg-white p-6 md:p-10">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-sm bg-[#0A0F0D] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#FFFFFF]">
                  {cat?.label || job.category}
                </span>
                <StatusPill status={job.status} />
              </div>
              <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#0A0F0D] sm:text-4xl">
                {job.title}
              </h1>
              <p className="mt-3 text-sm text-[#5C605E]">Posted by {job.client_name}</p>
            </div>
            {job.accepted_price != null && (
              <div
                className="rounded-md border border-[#0A0F0D] p-4 text-right"
                data-testid="job-accepted-price"
              >
                <p className="text-xs uppercase tracking-widest text-[#5C605E]">Accepted price</p>
                <p className="mt-1 font-display text-2xl font-bold text-[#0A0F0D]">
                  R{Number(job.accepted_price).toFixed(2)}
                </p>
                <p className="mt-1 text-[10px] uppercase tracking-widest text-[#5C605E]">
                  Escrow: {job.escrow_status}
                </p>
              </div>
            )}
          </div>

          <p className="mt-6 whitespace-pre-wrap text-base leading-relaxed text-[#0A0F0D]">
            {job.description}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-[#5C605E]">
            {job.budget && (
              <span className="inline-flex items-center gap-1.5">
                <CurrencyDollar size={14} weight="bold" /> Budget: {job.budget}
              </span>
            )}
            {(job.location?.address || job.location?.lat != null) && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={14} weight="bold" />
                {job.location.address || `${job.location.lat}°, ${job.location.lng}°`}
              </span>
            )}
          </div>

          {photos.length > 0 && (
            <div className="mt-8">
              <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-[#5C605E]">
                Photos ({photos.length})
              </p>
              <div
                className="grid grid-cols-3 gap-3 md:grid-cols-4"
                data-testid="job-photo-grid"
              >
                {photos.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setLightboxIndex(i);
                      setLightboxOpen(true);
                    }}
                    data-testid={`job-photo-${i}`}
                    className="group relative aspect-square overflow-hidden rounded-md border border-[#E5E2DB] bg-[#FFFFFF]"
                  >
                    <img
                      src={p.src}
                      alt=""
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-[#0A0F0D]/0 transition-colors group-hover:bg-[#0A0F0D]/30" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Client actions based on status */}
          {isClientOwner && (
            <div className="mt-8 flex flex-wrap gap-3" data-testid="client-actions">
              {job.status === "awaiting_payment" && (
                <Button
                  onClick={payEscrow}
                  data-testid="pay-escrow-button"
                  className="bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
                >
                  <Wallet size={16} weight="bold" className="mr-2" />
                  Pay R{Number(job.accepted_price).toFixed(2)} into escrow with Stripe
                </Button>
              )}
              {job.status === "awaiting_confirmation" && (
                <Button
                  onClick={confirmComplete}
                  data-testid="confirm-complete-button"
                  className="bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
                >
                  <CheckCircle size={16} weight="bold" className="mr-2" />
                  Confirm work is complete
                </Button>
              )}
            </div>
          )}

          {/* Contractor actions */}
          {isAssignedContractor && job.status === "in_progress" && (
            <div className="mt-8" data-testid="contractor-actions">
              <Button
                onClick={markComplete}
                data-testid="mark-complete-button"
                className="bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
              >
                <CheckCircle size={16} weight="bold" className="mr-2" />
                Mark job as complete
              </Button>
            </div>
          )}
        </div>

        {/* Quotes section */}
        <div className="mt-10 rounded-md border border-[#E5E2DB] bg-white p-6 md:p-10">
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0F0D]">
            Quotes {quotes.length > 0 && <span className="text-[#5C605E]">({quotes.length})</span>}
          </h2>

          {isContractor && !isClientOwner && !myQuote && job.status === "open" && (
            <form
              onSubmit={submitQuote}
              className="mt-6 rounded-md border border-dashed border-[#D6D3CD] bg-[#FFFFFF] p-5"
              data-testid="quote-form"
            >
              <p className="text-sm font-semibold text-[#0A0F0D]">Send your quote</p>
              <div className="mt-4 grid gap-4 md:grid-cols-[160px_1fr]">
                <div className="space-y-1.5">
                  <Label htmlFor="quote-price" className="text-[#0A0F0D]">
                    Price (R)
                  </Label>
                  <Input
                    id="quote-price"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={quotePrice}
                    onChange={(e) => setQuotePrice(e.target.value)}
                    placeholder="250"
                    data-testid="quote-price-input"
                    className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="quote-msg" className="text-[#0A0F0D]">
                    Message
                  </Label>
                  <Textarea
                    id="quote-msg"
                    required
                    rows={3}
                    value={quoteMessage}
                    onChange={(e) => setQuoteMessage(e.target.value)}
                    placeholder="Introduce yourself, timeline, materials, etc."
                    data-testid="quote-message-input"
                    className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-submit-button"
                  className="bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
                >
                  <ChatCircleText size={16} weight="bold" className="mr-2" />
                  {submitting ? "Sending…" : "Send quote"}
                </Button>
              </div>
            </form>
          )}

          {myQuote && (
            <p className="mt-4 rounded-md bg-[#FFFFFF] p-4 text-sm text-[#5C605E]">
              You submitted a quote of{" "}
              <strong className="text-[#0A0F0D]">R{Number(myQuote.price).toFixed(2)}</strong> on this job.
            </p>
          )}

          {quotes.length === 0 ? (
            <p className="mt-6 text-sm text-[#5C605E]">No quotes yet.</p>
          ) : (
            <ul className="mt-6 space-y-4" data-testid="quotes-list">
              {quotes.map((q) => (
                <li
                  key={q.id}
                  className="rounded-md border border-[#E5E2DB] p-4"
                  data-testid={`quote-item-${q.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-display text-base font-semibold text-[#0A0F0D]">
                          {q.business_name || q.contractor_name}
                        </p>
                        <TierBadge tier={q.tier || "none"} size="sm" testId={`quote-tier-${q.id}`} />
                        {q.id_verification_status === "verified" && (
                          <span
                            className="inline-flex items-center gap-1 rounded-sm bg-[#0A0F0D] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-white"
                            data-testid={`quote-verified-id-${q.id}`}
                          >
                            <ShieldCheck size={10} weight="fill" /> Verified ID
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-[#5C605E]">
                        {q.years_experience ? `${q.years_experience} yrs experience · ` : ""}
                        {q.contractor_name}
                      </p>
                    </div>
                    <p className="font-display text-xl font-bold text-[#0A0F0D]">
                      R{Number(q.price).toFixed(2)}
                    </p>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sm text-[#0A0F0D]">{q.message}</p>
                  {isClientOwner && job.status === "open" && (
                    <div className="mt-4 flex justify-end">
                      <Button
                        size="sm"
                        onClick={() => acceptQuote(q.id)}
                        data-testid={`accept-quote-${q.id}`}
                        className="bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
                      >
                        <ShieldCheck size={14} weight="bold" className="mr-2" />
                        Accept this quote
                      </Button>
                    </div>
                  )}
                  {isClientOwner && job.accepted_quote_id === q.id && (
                    <p className="mt-3 inline-flex items-center gap-1 rounded-sm bg-[#0A0F0D] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#FFFFFF]">
                      <CheckCircle size={10} weight="bold" /> Accepted
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
};

export default JobDetailPage;
